import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import moment from "moment";
import { Fragment, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";

const DispatchUpdated = () => {
    const privileges = secureLocalStorage.getItem("privileges");
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialQueries = searchParams.get("q")?.split(",") || [];
    const initialColumns = searchParams.get("c")?.split(",") || [];

    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");

    const TypeMasterQuery = useAPIQuery({
        key: "api/type-master",
        endpoint: "/api/type-master/",
    });

    const TypeMasterQueryData = TypeMasterQuery.useFetch({
        params: {
            q: "Dispatch Status",
            c: "category",
        },
    });

    const { useFetch: useFetchSalesOrders } = useAPIQuery({
        key: "api/sales-order",
        endpoint: "/api/sales-order/",
    });

    const { data, isFetching, isError } = useFetchSalesOrders({
        params: {
            entries: entries,
            page_no: pageIndex + 1,
            q: globalFilterValue !== "" ? globalFilterValue : columnFilterValue,
            c: globalFilterValue === "" ? columnFilterKey : "",
            action: "dispatch",
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const columns = [
        {
            accessor: "so_number",
            header: "Sales Order number",
            cell: (row) => (
                <Fragment>
                    {row?.dispatched_orders?.length > 0 &&
                    privileges.includes("dispatch_details") ? (
                        <span
                            className="link link-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                                navigate("/dispatch-details", {
                                    state: { salesOrderDetails: row },
                                })
                            }
                        >
                            {row?.so_number}
                        </span>
                    ) : (
                        <span>{row?.so_number}</span>
                    )}
                </Fragment>
            ),
        },
        {
            accessor: "client",
            header: "Client Name",
            cell: (row) => <>{`${row?.client_details?.name}`}</>,
        },
        {
            accessor: "product",
            header: "Product Name",
            cell: (row) => <>{row?.product_details?.product_name}</>,
        },
        {
            accessor: "target_date",
            header: "Target Date",
            cell: (row) => <>{moment(row?.target_date).format("DD-MM-YYYY")}</>,
        },
        {
            accessor: "dispatch_location",
            header: "Dispatch Location",
        },
        {
            accessor: "quantity",
            header: "Quantity To Be Produced",
        },
        {
            accessor: "quantity_produced",
            header: "Quantity Produced",
            cell: (row) => (
                <>
                    {row?.product_details?.quantity_available_for_sales_order +
                        row?.total_dispatched}
                </>
            ),
        },
        {
            accessor: "quantity_produced",
            header: "Quantity Available for Dispatch",
            cell: (row) => (
                <>{row?.product_details?.quantity_available_for_sales_order}</>
            ),
        },
        {
            accessor: "total_dispatched",
            header: "Dispatched Quantity",
        },
        {
            accessor: "activity_status",
            header: "Status",
            cell: (row) => (
                <>
                    {row?.activity_status === 1 ? (
                        <span className="badge bg-warning">ongoing</span>
                    ) : row?.activity_status === 4 ? (
                        <span className="badge bg-success">dispatched</span>
                    ) : row?.activity_status === 3 ? (
                        <span className="badge bg-info">Completed</span>
                    ) : row?.activity_status === 5 ? (
                        <span className="badge bg-info">Partial</span>
                    ) : row?.activity_status === 6 ? (
                        <span className="badge bg-primary">Material Available</span>
                    ) : null}
                </>
            ),
            filterable: true,
            filterType: "dropdown",
            filterOpt: (() => {
                const fromTypeMaster = TypeMasterQueryData.data
                    ? TypeMasterQueryData.data?.results?.data?.map((type) => ({
                          label: type?.name,
                          value: type?.value,
                      })) ?? []
                    : [];
                const hasMaterialAvailable = fromTypeMaster.some(
                    (opt) => String(opt?.value) === "6"
                );
                return hasMaterialAvailable
                    ? fromTypeMaster
                    : [
                          ...fromTypeMaster,
                          { label: "Material Available", value: "6" },
                      ];
            })(),
        },
    ];

    const resetToDefaultDispatchFilter = () => {
        setGlobalFilterValue("");
        setColumnFilterKey("activity_status");
        setColumnFilterValue("1,6");
        setSearchParams((params) => {
            params.set("q", "1,6");
            params.set("c", "activity_status");
            return params;
        });
    };

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        if (q !== "") {
            setGlobalFilterValue(q);
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.delete("c");
                return searchParams;
            });
        } else if (q === "") {
            resetToDefaultDispatchFilter();
        }
    };

    const handleColumnFilterChange = (value) => {
        if (!value?.length) {
            resetToDefaultDispatchFilter();
            return;
        }

        const ids = value.map((val) => val.id);
        const vals = value
            .map((val) => val.value?.trim() || "")
            .filter((v) => v !== "");
        const uniqueIds = [...new Set(ids)];

        // Same column multi-select (e.g. statuses 1 and 6) → q=1,6&c=activity_status
        const q =
            uniqueIds.length === 1
                ? vals.join(",")
                : vals.join(",");
        const c =
            uniqueIds.length === 1
                ? uniqueIds[0]
                : ids.join(",");

        if (q !== "" && c !== "") {
            setGlobalFilterValue("");
            setColumnFilterKey(c);
            setColumnFilterValue(q);
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.set("c", c);
                return searchParams;
            });
        } else {
            resetToDefaultDispatchFilter();
        }
    };

    const handlePagination = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set("entries", value.pageSize);
            searchParams.set("page", value.pageIndex + 1);
            return searchParams;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleEditRow = (row) => {
        console.log(row);
        navigate("/dispatch-sales-order-update", {
            state: { editpost: row.original },
        });
    };

    if (isError) return <div>Error loading dispatched orders.</div>;

    return (
        <Fragment>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            Dispatched Orders
                        </h3>
                    </div>
                </div>
            </div>
            <Table
                data={data ? data?.results.data : []}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={data ? data.count : 0}
                globalSearch
                isLoading={isFetching}
                editRows={
                    privileges.includes("sales_order_dispatch_update")
                        ? true
                        : false
                }
                defaultGlobalSearchValue={
                    initialQueries.length > 0 && initialColumns.length <= 0
                        ? initialQueries[0]
                        : undefined
                }
                defaultColumnFiltersValue={
                    initialQueries.length > 0 && initialColumns.length > 0
                        ? initialColumns.length === 1
                            ? [
                                  {
                                      id: initialColumns[0],
                                      value: initialQueries.join(","),
                                  },
                              ]
                            : initialColumns.map((column, index) => ({
                                  id: column,
                                  value: initialQueries?.[index] ?? "",
                              }))
                        : undefined
                }
                deleteRows={false}
                filterModal={false}
                selectRows={false}
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFilterChange={handleColumnFilterChange}
                onRowEdit={handleEditRow}
                onPaginationChange={handlePagination}
            />
        </Fragment>
    );
};

export default DispatchUpdated;
